/** Shared three.js helpers for the UI's 3D views (title, codex/ending pedestal, transitions). */
import {
  AdditiveBlending,
  Box3,
  BufferAttribute,
  BufferGeometry,
  Color,
  DoubleSide,
  Group,
  IcosahedronGeometry,
  InstancedMesh,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  Points,
  SphereGeometry,
  Vector3,
  CylinderGeometry,
  MeshBasicMaterial,
  PlaneGeometry,
} from 'three';
import { InstancedBufferAttribute, Sprite, Texture, type Material } from 'three';
import { MeshBasicNodeMaterial, PointsNodeMaterial } from 'three/webgpu';
import {
  atan,
  cameraPosition,
  clamp,
  cos,
  dot,
  float,
  instancedBufferAttribute,
  length,
  mix,
  mx_noise_float,
  normalWorld,
  positionWorld,
  sin,
  smoothstep,
  uniform,
  uv,
  vec3,
} from 'three/tsl';
import { MeshSurfaceSampler } from 'three/examples/jsm/math/MeshSurfaceSampler.js';
import type { Assets } from '@core/assets';
import type { QualityProfile } from '@core/quality';
import { TREE, type StageId } from '@core/tree';
import { PREVIEW_LOADERS } from '@stages/registry';

export interface PreviewDeps {
  assets: Assets;
  quality: QualityProfile;
}

/** Fallback when a preview fails to load: glowing blob in the stage colour. */
function fallbackPreview(id: StageId): Object3D {
  const g = new Group();
  const c = new Color(TREE[id].color);
  g.add(new Mesh(new IcosahedronGeometry(0.5, 3), new MeshStandardMaterial({ color: c, emissive: c, emissiveIntensity: 0.35, roughness: 0.35 })));
  const eye = new Mesh(new SphereGeometry(0.1, 12, 12), new MeshStandardMaterial({ color: 0xffffff }));
  eye.position.set(0, 0.15, 0.42);
  g.add(eye);
  return g;
}

/** Load a stage's preview model, normalised to fit a unit box and centred at the origin. */
export async function loadPreview(id: StageId, deps: PreviewDeps, size = 1): Promise<Object3D> {
  let obj: Object3D;
  try {
    const mod = await PREVIEW_LOADERS[id]();
    obj = await mod.createPreview({ assets: deps.assets, quality: deps.quality });
  } catch (err) {
    console.warn(`[ui] preview for ${id} failed`, err);
    obj = fallbackPreview(id);
  }
  // Preview models belong to the stage modules (module-level caches) and the asset cache, and the
  // next stage often reuses the same geometry/materials. UI scenes must never dispose them —
  // on the WebGPU backend that leaves live meshes pointing at destroyed buffers (#143).
  markShared(obj);
  const wrap = new Group();
  wrap.name = `preview:${id}`;
  wrap.add(obj);
  obj.updateMatrixWorld(true);
  const box = new Box3().setFromObject(obj);
  if (!box.isEmpty()) {
    const dims = box.getSize(new Vector3());
    const s = size / Math.max(1e-3, dims.x, dims.y, dims.z);
    const centre = box.getCenter(new Vector3());
    obj.position.sub(centre.multiplyScalar(s));
    obj.scale.multiplyScalar(s);
  }
  return wrap;
}

/** Sample `count` points on the surfaces of all meshes under `root` (in root's local space). */
export function samplePoints(root: Object3D, count: number, fallbackRadius = 0.5): Float32Array {
  const out = new Float32Array(count * 3);
  root.updateMatrixWorld(true);
  const meshes: Mesh[] = [];
  root.traverse((o) => {
    const m = o as Mesh;
    if (m.isMesh && m.geometry?.attributes?.position && m.visible) meshes.push(m);
  });
  const inv = root.matrixWorld.clone().invert();
  const tmp = new Vector3();
  if (!meshes.length) {
    for (let i = 0; i < count; i++) {
      tmp.randomDirection().multiplyScalar(fallbackRadius * Math.cbrt(Math.random()));
      tmp.toArray(out, i * 3);
    }
    return out;
  }
  const weights = meshes.map((m) => {
    const g = m.geometry;
    return g.index ? g.index.count / 3 : g.attributes.position.count / 3;
  });
  const total = weights.reduce((a, b) => a + b, 0) || 1;
  let k = 0;
  meshes.forEach((m, mi) => {
    const n = mi === meshes.length - 1 ? count - k : Math.round((weights[mi] / total) * count);
    if (n <= 0) return;
    let sampler: MeshSurfaceSampler | undefined;
    try {
      sampler = new MeshSurfaceSampler(m).build();
    } catch {
      sampler = undefined;
    }
    for (let i = 0; i < n && k < count; i++, k++) {
      if (sampler) sampler.sample(tmp);
      else tmp.randomDirection().multiplyScalar(fallbackRadius);
      tmp.applyMatrix4(m.matrixWorld).applyMatrix4(inv);
      tmp.toArray(out, k * 3);
    }
  });
  for (; k < count; k++) {
    tmp.randomDirection().multiplyScalar(fallbackRadius);
    tmp.toArray(out, k * 3);
  }
  return out;
}

/** Material whose animated inputs are exposed as `uniforms.<name>.value` (TSL uniform nodes). */
export type UniformMaterial = Material & { uniforms: Record<string, { value: any }> };

/** Shorthand for scene code: `uni(mesh).uTime.value = t`. */
export function uni(o: { material: Material | Material[] }): Record<string, { value: any }> {
  return (o.material as UniformMaterial).uniforms;
}

/**
 * Floating motes that drift upward and wrap (y in [-10, 10]) — instanced sprites with a
 * PointsNodeMaterial (WebGPU `Points` are 1 px, so sized particles are sprites with `count`).
 */
export function makeMotes(count: number, spread: Vector3, colorHex: number, size = 0.5): Sprite {
  const base = new Float32Array(count * 3);
  const seedArr = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    base[i * 3] = (Math.random() - 0.5) * spread.x;
    base[i * 3 + 2] = (Math.random() - 0.5) * spread.z;
    seedArr[i] = Math.random();
  }
  const uTime = uniform(0);
  const uColor = uniform(new Color(colorHex));
  const uOpacity = uniform(0.8);
  const p0 = instancedBufferAttribute(new InstancedBufferAttribute(base, 3), 'vec3') as unknown as ReturnType<typeof vec3>;
  const seed = instancedBufferAttribute(new InstancedBufferAttribute(seedArr, 1), 'float') as unknown as ReturnType<typeof float>;
  const mat = new PointsNodeMaterial({ transparent: true, depthWrite: false, blending: AdditiveBlending, sizeAttenuation: true });
  mat.positionNode = vec3(
    p0.x.add(sin(uTime.mul(0.3).add(seed.mul(6.28))).mul(0.3)),
    uTime.mul(seed.mul(0.12).add(0.08)).add(seed.mul(20)).mod(20).sub(10),
    p0.z,
  );
  mat.sizeNode = float(size * 0.16).mul(seed.add(0.5));
  const a = smoothstep(0.5, 0.0, length(uv().sub(0.5)));
  const twinkle = sin(uTime.mul(seed.mul(2).add(1)).add(seed.mul(30))).mul(0.45).add(0.55);
  mat.colorNode = uColor.mul(a.add(0.6));
  mat.opacityNode = a.mul(a).mul(uOpacity).mul(twinkle);
  (mat as unknown as UniformMaterial).uniforms = { uTime, uColor, uOpacity };
  const sprite = new Sprite(mat);
  sprite.count = count;
  sprite.frustumCulled = false;
  return sprite;
}

/** Swirling portal disc (TSL). Animate via `uni(portal).uTime/uIntensity/uA/uB`. */
export function makePortal(radius: number, colorA = 0x97ff5a, colorB = 0x1fd18a): Mesh<PlaneGeometry, MeshBasicNodeMaterial> {
  const uTime = uniform(0);
  const uIntensity = uniform(1);
  const uA = uniform(new Color(colorA));
  const uB = uniform(new Color(colorB));
  const p = uv().mul(2).sub(1);
  const r = length(p);
  const ang = atan(p.y, p.x);
  const swirl = ang.add(r.mul(5)).sub(uTime.mul(0.9));
  // Noise sampled on a circle (cos/sin of the swirl angle) → no seam at ±π.
  const n = mx_noise_float(vec3(cos(swirl).mul(1.6), sin(swirl).mul(1.6), r.mul(4).sub(uTime.mul(0.6))))
    .mul(0.5)
    .add(0.5)
    .mul(0.6)
    .add(mx_noise_float(vec3(cos(swirl).mul(3.5), sin(swirl).mul(3.5), r.mul(9))).mul(0.2).add(0.2));
  const arms = sin(swirl.mul(3).add(n.mul(4))).mul(0.5).add(0.5);
  const rim = smoothstep(0.62, 0.9, r).mul(smoothstep(1.0, 0.9, r));
  const body = smoothstep(1.0, 0.2, r).mul(arms.mul(n).mul(0.65).add(0.35));
  const core = smoothstep(0.35, 0.0, r);
  const col = mix(uB, uA, arms).mul(body.mul(1.1).add(rim.mul(2.2))).add(vec3(0.8, 1.0, 0.7).mul(core.mul(0.8)));
  const alpha = clamp(body.add(rim).add(core.mul(0.5)), 0, 1).mul(smoothstep(1.0, 0.94, r));
  const mat = new MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: DoubleSide, blending: AdditiveBlending });
  mat.colorNode = col.mul(uIntensity).mul(0.6);
  mat.opacityNode = alpha.mul(uIntensity);
  (mat as unknown as UniformMaterial).uniforms = { uTime, uIntensity, uA, uB };
  return new Mesh(new PlaneGeometry(radius * 2, radius * 2), mat);
}

/** Double-helix DNA made of instanced spheres + rungs. Rotate the returned group. */
export function makeHelix(opts: { turns: number; height: number; radius: number; perTurn: number; colorA?: number; colorB?: number; rungs?: boolean; dot?: number }): Group {
  const g = new Group();
  const n = Math.round(opts.turns * opts.perTurn);
  const sphere = new SphereGeometry(1, 12, 10);
  const matA = new MeshBasicMaterial({ color: opts.colorA ?? 0x97ff5a });
  const matB = new MeshBasicMaterial({ color: opts.colorB ?? 0x5bf0ff });
  const a = new InstancedMesh(sphere, matA, n);
  const b = new InstancedMesh(sphere, matB, n);
  const rung = opts.rungs !== false ? new InstancedMesh(new CylinderGeometry(1, 1, 1, 6), new MeshBasicMaterial({ color: opts.colorA ?? 0x97ff5a, transparent: true, opacity: 0.35, blending: AdditiveBlending, depthWrite: false }), n) : undefined;
  const d = new Object3D();
  const pa = new Vector3();
  const pb = new Vector3();
  for (let i = 0; i < n; i++) {
    const t = i / n;
    const ang = t * opts.turns * Math.PI * 2;
    const y = (t - 0.5) * opts.height;
    pa.set(Math.cos(ang) * opts.radius, y, Math.sin(ang) * opts.radius);
    pb.set(Math.cos(ang + Math.PI) * opts.radius, y, Math.sin(ang + Math.PI) * opts.radius);
    const s = opts.dot ?? opts.radius * 0.16;
    d.position.copy(pa);
    d.scale.setScalar(s);
    d.rotation.set(0, 0, 0);
    d.updateMatrix();
    a.setMatrixAt(i, d.matrix);
    d.position.copy(pb);
    d.updateMatrix();
    b.setMatrixAt(i, d.matrix);
    if (rung && i % 2 === 0) {
      d.position.copy(pa).add(pb).multiplyScalar(0.5);
      d.scale.set(opts.radius * 0.012, opts.radius * 2, opts.radius * 0.012);
      d.lookAt(pb);
      d.rotateX(Math.PI / 2);
      d.updateMatrix();
      rung.setMatrixAt(i, d.matrix);
    } else if (rung) {
      d.scale.setScalar(0);
      d.updateMatrix();
      rung.setMatrixAt(i, d.matrix);
    }
  }
  g.add(a, b);
  if (rung) g.add(rung);
  return g;
}

/** Fresnel "membrane" material for translucent cells (instancing + instanceColor via node material). */
export function membraneMaterial(): MeshBasicNodeMaterial & UniformMaterial {
  const uTime = uniform(0);
  const mat = new MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: AdditiveBlending }) as MeshBasicNodeMaterial & UniformMaterial;
  const viewDir = cameraPosition.sub(positionWorld).normalize();
  const f = float(1).sub(dot(normalWorld, viewDir).abs()).pow(2.2);
  mat.colorNode = vec3(1).mul(f.mul(1.4).add(0.08)); // multiplied by instanceColor automatically
  mat.opacityNode = f.mul(0.8).add(0.12);
  mat.uniforms = { uTime };
  return mat;
}

export { disposeObject as disposeTree } from '@core/utils/dispose';

/** Flag every geometry/material/texture under `root` as shared so disposeTree() skips it. */
function markShared(root: Object3D): void {
  root.traverse((o) => {
    const m = o as Mesh;
    if (m.geometry) m.geometry.userData.evoShared = true;
    const mats = (Array.isArray(m.material) ? m.material : m.material ? [m.material] : []) as Material[];
    for (const mat of mats) {
      mat.userData.evoShared = true;
      for (const v of Object.values(mat as unknown as Record<string, unknown>)) {
        if (v instanceof Texture) v.userData.evoShared = true;
      }
    }
  });
}
